import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { AddConversationTagState } from "@redotech/redo-return-ui/flows/chat-flow/add-conversation-tag";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { RuleTagPicker } from "../rules/rule-tag-picker";

export const AddConversationTagDetails = memo(
  function AddConversationTagDetails({
    stepSelect,
    state,
    setState,
  }: StepTypeDetailsProps<AddConversationTagState>) {
    const { t } = useTranslation();
    const canEditSupportSettings = useHasPermission(
      SettingsPermissions.SET_SUPPORT_SETTINGS,
    );

    const selectedTagNames = useMemo(
      () => state.tags.map((tag) => tag.name),
      [state.tags],
    );

    const handleTagNamesChange = useHandler((names: string[]) => {
      setState((state) => ({
        ...state,
        tags: names.map((name) => ({
          name,
          color: state.tags.find((tag) => tag.name === name)?.color ?? "",
        })),
      }));
    });

    const nextStepChange = useHandler((next?: StepId) => {
      setState((state) => ({ ...state, next }));
    });

    return (
      <>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Conversation tags")}
          </Text>
          <Flex
            style={
              canEditSupportSettings
                ? undefined
                : { pointerEvents: "none", opacity: 0.6 }
            }
          >
            <RuleTagPicker
              onChange={handleTagNamesChange}
              selectedTagNames={selectedTagNames}
            />
          </Flex>
          <Text fontSize="xxs" fontWeight="regular" textColor="secondary">
            {t(
              "The tags added to the conversation when it reaches this step. Use them to internally route the conversation before handoff.",
            )}
          </Text>
        </Flex>

        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Next step")}
          </Text>
          {stepSelect({ value: state.next, valueChange: nextStepChange })}
          {!state.next && (
            <Text fontSize="xs" textColor="error">
              {t("Required")}
            </Text>
          )}
        </Flex>
      </>
    );
  },
);
